import * as React from 'react';

import { AsyncBoundary, Col } from '@rematter/ui-web';
import { useToast } from '@rematter/ui-web/hooks';
import { useAtom, useAtomValue, useSetAtom } from 'jotai';

import { $saveCurrentTemplateSetting } from '../../../_graphql/mutationSaveFacilityTemplateSetting';
import {
  $fileTemplates,
  $hasUnsavedChanges,
  $isSaving,
  $resetTemplateSelection,
  $selectedTemplateId,
  $selectedTemplateType,
  $templateOptions,
  $templatePreviewQuery,
} from '../../../_state/locationDocumentTemplate.state';
import { $facilityId } from '../../../_state/locations.state';

import { TemplatePreview } from './TemplatePreview';
import { TemplateSelector } from './TemplateSelector';

export const TemplateFileViewer = () => {
  const toast = useToast();

  const facilityId = useAtomValue($facilityId);
  const selectedTemplateType = useAtomValue($selectedTemplateType);
  const [selectedTemplateId, setSelectedTemplateId] =
    useAtom($selectedTemplateId);
  const fileTemplates = useAtomValue($fileTemplates);
  const templateOptions = useAtomValue($templateOptions);
  const hasUnsavedChanges = useAtomValue($hasUnsavedChanges);
  const resetTemplateSelection = useSetAtom($resetTemplateSelection);
  const saveCurrentTemplateSetting = useSetAtom($saveCurrentTemplateSetting);
  const [isSaving, setIsSaving] = useAtom($isSaving);
  const {
    data: previewUrl,
    isLoading: isLoadingPreview,
    error: previewError,
  } = useAtomValue($templatePreviewQuery);

  const handleTemplateChange = React.useCallback(
    (templateId: string | undefined) => {
      setSelectedTemplateId(templateId);
    },
    [setSelectedTemplateId],
  );

  const handleSave = React.useCallback(async () => {
    if (!facilityId) return;

    setIsSaving(true);
    try {
      await saveCurrentTemplateSetting(facilityId);
      toast.success('Template saved successfully');
    } catch (error) {
      toast.error('Failed to save template');
      console.error('Failed to save template:', error);
    } finally {
      setIsSaving(false);
    }
  }, [facilityId, saveCurrentTemplateSetting, toast, setIsSaving]);

  const handleReset = React.useCallback(() => {
    resetTemplateSelection();
  }, [resetTemplateSelection]);

  return (
    <Col className="@TemplateFileViewer h-full flex-1 gap-4 p-5 pl-0" yStart>
      <Col className="h-full overflow-hidden rounded-lg border border-neutral/10">
        <TemplateSelector
          selectedType={selectedTemplateType}
          selectedTemplateId={selectedTemplateId}
          templateOptions={templateOptions}
          isLoadingTemplates={fileTemplates.isLoading}
          hasUnsavedChanges={hasUnsavedChanges}
          isSaving={isSaving}
          onTemplateChange={handleTemplateChange}
          onSave={handleSave}
          onReset={handleReset}
        />

        <AsyncBoundary
          loading={fileTemplates.isLoading || isLoadingPreview}
          error={fileTemplates.error || previewError}
        >
          <TemplatePreview previewUrl={previewUrl} />
        </AsyncBoundary>
      </Col>
    </Col>
  );
};
